import { useState, useEffect, useCallback } from 'react';
import { useFormContext, Controller } from 'react-hook-form';

import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { FilePicker } from '@/components/FilePicker/FilePicker';
import { FormAutocompleteField } from '@/components/FormElements';
import {
  Card,
  Label,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/TailwindComponents';
import { IACaseType, IACaseStatus } from '@/models';

import { IAFileAcceptedTypes } from './data';

// Case type options matching the IACaseType enum
const caseTypes = [
  { value: IACaseType.USE_OF_FORCE, label: 'Use of Force' },
  { value: IACaseType.OFFICER_MISCONDUCT, label: 'Officer Misconduct' },
  { value: IACaseType.CITIZEN_COMPLAINT, label: 'Citizen Complaint' },
  { value: IACaseType.INTERNAL_COMPLAINT, label: 'Internal Complaint' },
  { value: IACaseType.POLICY_VIOLATION, label: 'Policy Violation' },
];

// Status options matching the IACaseStatus enum
const statusOptions = [
  { value: IACaseStatus.OPEN, label: 'Open' },
  { value: IACaseStatus.IN_PROGRESS, label: 'In Progress' },
  { value: IACaseStatus.CLOSED, label: 'Closed' },
];

interface IAOfficer {
  id: string;
  name: string;
  email?: string;
}

export const InternalAffairsOverviewForm = () => {
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const [availableOfficers, setAvailableOfficers] = useState<IAOfficer[]>([]);
  const [isLoadingOfficers, setIsLoadingOfficers] = useState(false);

  // Watch the files field for initial values
  const currentFiles = watch('files');

  // Fetch IA Officers from API
  const fetchIAOfficers = useCallback(async () => {
    setIsLoadingOfficers(true);
    try {
      const { data } = await InternalAffairsApi.getIAOfficers();
      setAvailableOfficers(data);
    } catch (error) {
      console.error('Error fetching IA officers:', error);
    } finally {
      setIsLoadingOfficers(false);
    }
  }, []);

  useEffect(() => {
    fetchIAOfficers();
  }, [fetchIAOfficers]);

  return (
    <div
      className="mt-6 h-screen overflow-auto scrollbar-hidden"
      style={{
        overflow: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {/* Main Form Section */}
      <Card className="!border-0 p-5 mb-4 rounded-lg max-w-[708px]">
        <h6 className="mb-4 text-base font-semibold">General information</h6>

        <div className="border-t mb-4 w-full border-grey-20"></div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
          {/* Case Type */}
          <div className="col-span-1">
            <Label htmlFor="caseType">
              Case type<span className="text-red-600">*</span>
            </Label>
            <Controller
              name="caseType"
              control={control}
              rules={{ required: 'Case type is required' }}
              render={({ field }) => (
                <div className="mt-1">
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a case type" />
                    </SelectTrigger>
                    <SelectContent>
                      {caseTypes.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.caseType && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.caseType.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Status */}
          <div className="col-span-1">
            <Label htmlFor="status">Status</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div className="mt-1">
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
          </div>
        </div>

        {/* Case Details */}
        <div className="mb-2">
          <Label htmlFor="caseDetails">
            Case details<span className="text-red-600">*</span>
          </Label>
          <Textarea
            id="caseDetails"
            rows={4}
            {...register('caseDetails', {
              required: 'Case details are required',
            })}
            className="mt-1"
            placeholder="/"
          />
          {errors.caseDetails && (
            <p className="text-sm text-red-600 mt-1">
              {errors.caseDetails.message as string}
            </p>
          )}
        </div>

        {/* Investigation Notes */}
        <div className="mb-2">
          <Label htmlFor="investigationNotes">Investigation notes</Label>
          <Textarea
            id="investigationNotes"
            rows={4}
            {...register('investigationNotes')}
            className="mt-1"
            placeholder="/"
          />
        </div>

        {/* Officer Details */}
        <div className="mb-2">
          <Label htmlFor="officerDetails">
            Officer details<span className="text-red-600">*</span>
          </Label>
          <Textarea
            id="officerDetails"
            rows={4}
            {...register('officerDetails', {
              required: 'Officer details are required',
            })}
            className="mt-1"
            placeholder="/"
          />
          {errors.officerDetails && (
            <p className="text-sm text-red-600 mt-1">
              {errors.officerDetails.message as string}
            </p>
          )}
        </div>

        {/* Findings and Recommendations */}
        <div className="mb-2">
          <Label htmlFor="findingsRecommendations">
            Findings and recommendations
          </Label>
          <Textarea
            id="findingsRecommendations"
            rows={4}
            {...register('findingsRecommendations')}
            className="mt-1"
            placeholder="/"
          />
        </div>

        {/* Assign Users - Multi-select dropdown */}
        <div>
          <Label htmlFor="assignedOfficers">Assign users</Label>
          <FormAutocompleteField
            name="assignedOfficers"
            items={availableOfficers.map((officer) => ({
              value: officer.id,
              label: officer.name,
            }))}
            multiple
            limitTags={2}
            disabled={isLoadingOfficers}
          />
        </div>
      </Card>

      {/* Media and Documents Section */}
      <Card className="!border-0 p-5 mb-4 rounded-lg max-w-[708px]">
        <h6 className="mb-4 text-base font-semibold">Media and documents</h6>

        <div className="border-t mb-4 w-full border-grey-20"></div>

        <Controller
          name="files"
          control={control}
          render={({ field }) => (
            <FilePicker
              initialFileItems={currentFiles || []}
              onChange={field.onChange}
              accept={IAFileAcceptedTypes}
              direction="vertical"
              enableEditDescription={true}
            />
          )}
        />
      </Card>
    </div>
  );
};
